import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLegacyAuth } from '../../hooks/useLegacyAuth';
import './GameOverModal.css';

interface GameOverModalProps {
  score: number;
  highestTile: number;
  won: boolean;
  moveCount: number;
  onClose: () => void;
  saveStatus?: 'saving' | 'success' | 'error' | null;
  saveError?: string | null;
}

const GameOverModal: React.FC<GameOverModalProps> = ({
  score,
  highestTile,
  won,
  moveCount,
  onClose,
  saveStatus = null,
  saveError = null
}) => {
  // Get auth state via legacy hook (supports both new auth and legacy localStorage)
  const { twitterUser } = useLegacyAuth();
  const navigate = useNavigate();
  const [buttonsEnabled, setButtonsEnabled] = useState(false);

  // Enable buttons after a brief delay to prevent accidental clicks
  useEffect(() => {
    const timer = setTimeout(() => {
      setButtonsEnabled(true);
    }, 1000); // 1 second delay

    return () => clearTimeout(timer);
  }, []);

  const handleShare = () => {
    const statusText = won
      ? `🎉 I reached the ${highestTile} tile!`
      : `I scored ${score.toLocaleString()} points in Alien 2048!`;

    const tweetText = `${statusText}\n\n` +
      `Final Score: ${score.toLocaleString()}\n` +
      `Highest Tile: ${highestTile}\n` +
      `Total Moves: ${moveCount}\n\n` +
      `Play Alien 2048 on mfers arcade! 👽🎮`;

    const gameUrl = `${window.location.origin}/alien2048/play`;
    const twitterIntentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(gameUrl)}`;
    window.open(twitterIntentUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="alien2048-gameover-overlay" onClick={onClose}>
      <div className="alien2048-gameover-content" onClick={e => e.stopPropagation()}>
        <div className={`alien2048-gameover-title ${won ? 'win' : 'lose'}`}>
          {won ? '🎉 Congratulations!' : '😵 Game Over'}
        </div>
        <div className="alien2048-gameover-subtitle">
          {won
            ? `You reached the ${highestTile} tile!`
            : 'No more moves available!'}
        </div>

        <div className="alien2048-gameover-stats">
          <table>
            <tbody>
              <tr>
                <td>Final Score</td>
                <td>{score.toLocaleString()}</td>
              </tr>
              <tr>
                <td>Highest Tile</td>
                <td>{highestTile}</td>
              </tr>
              <tr>
                <td>Total Moves</td>
                <td>{moveCount}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Save status indicator */}
        <div className="alien2048-save-status" data-testid="save-status">
          {saveStatus === 'saving' && (
            <div className="alien2048-save-status-message saving">
              💾 Saving score to leaderboard...
            </div>
          )}
          {saveStatus === 'success' && (
            <div className="alien2048-save-status-message success" data-testid="save-success">
              ✅ Score saved to leaderboard!
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="alien2048-save-status-message error" data-testid="save-error">
              ❌ {saveError || 'Failed to save score to leaderboard'}
            </div>
          )}
        </div>

        <div className="alien2048-gameover-buttons">
          <button className="alien2048-gameover-button alien2048-play-again-button" onClick={() => window.location.reload()} disabled={!buttonsEnabled}>
            Play Again
          </button>
          <button className="alien2048-gameover-button alien2048-leaderboard-button" onClick={() => navigate('/alien2048/leaderboard')} disabled={!buttonsEnabled}>
            View Leaderboard
          </button>
          <button className="alien2048-gameover-button alien2048-arcade-button" onClick={() => navigate('/')} disabled={!buttonsEnabled}>
            Back to Arcade
          </button>
          <button className="alien2048-gameover-button alien2048-share-button" onClick={handleShare} disabled={!buttonsEnabled}>
            <img
              src="/images/x-logo-white.png"
              alt="X"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            Share on 𝕏
          </button>
        </div>

        {/* Loading overlay - blocks clicks until ready */}
        {!buttonsEnabled && (
          <div className="alien2048-loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameOverModal;
